import type { MorphemeHrefObject } from "@-/dev.improve.haniverse.lib.morpheme.href.entities";
import type {
  MorphemePanelsMnemonicsNavigationProps,
  MorphemePanelsToolbarEditAction,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels";
import { useMemo } from "react";

/**
 * Per-morpheme panel chrome shared by the grapheme AND word panels: which morpheme, its href, the
 * mnemonics pager navigation and the toolbar's edit action. It deliberately carries no slice data:
 * the grapheme slices live in `GraphemeMorphemePanelsContext`, published by the gate that narrowed
 * them, so a word subtree never sees a grapheme-shaped value it cannot fill.
 */
export function useMorphemePanelsContextValue({
  activeIndex,
  character,
  isFlashcardEditPending = false,
  morphemeHrefObject,
  onFlashcardEditPendingChange,
  onToolbarEditActionChange,
  setActiveIndex,
  toolbarEditAction,
  viewNavigationRef,
}: {
  character: string;
  /**
   * Set by a card-actions menu that has no edit action to run (Details publishes none): the
   * mnemonics pager opens the meaning reading, whose edit action then starts editing and clears it.
   */
  isFlashcardEditPending?: boolean;
  morphemeHrefObject: MorphemeHrefObject;
  onFlashcardEditPendingChange?: (isPending: boolean) => void;
  onToolbarEditActionChange?: (action: MorphemePanelsToolbarEditAction) => void;
  toolbarEditAction?: MorphemePanelsToolbarEditAction;
} & MorphemePanelsMnemonicsNavigationProps) {
  return useMemo(
    () => ({
      activeIndex,
      character,
      isFlashcardEditPending,
      morphemeHrefObject,
      onFlashcardEditPendingChange,
      onToolbarEditActionChange,
      setActiveIndex,
      toolbarEditAction: toolbarEditAction ?? null,
      viewNavigationRef,
    }),
    [
      activeIndex,
      character,
      isFlashcardEditPending,
      morphemeHrefObject,
      onFlashcardEditPendingChange,
      onToolbarEditActionChange,
      setActiveIndex,
      toolbarEditAction,
      viewNavigationRef,
    ],
  );
}

export type MorphemePanelsContextValue = ReturnType<typeof useMorphemePanelsContextValue>;
